import {MapService, Markers, gmAPI} from '@/services/MapService';
import {Graphics} from '@/services/GraphicService';
import {BeyondMapsPoly} from '@/services/PolyService';
import {reactive} from 'vue';
import {geohashForLocation} from 'geofire-common';
import {orange} from '@/lib/colors';
import {DisplayField} from '@/lib/classes/InfoWindowContent';
import {calculateSF, calculateTotalAcreage} from './PolygonGraphic';
import {calculateTotalDistance} from './PolylineGraphic';

type PolyGraphicData = {
  bounds: google.maps.LatLngBoundsLiteral;
  featureType: string;
  clickable: boolean;
  path: google.maps.LatLngLiteral[];
  strokeColor: string;
};

type PolyData = {
  name: string;
  bounds: google.maps.LatLngBoundsLiteral;
  geohash: string;
  graphicData: PolyGraphicData;
  displayFields: DisplayField[];
};

type PolyOptions = google.maps.PolygonOptions & {
  calculateSF?: boolean;
};

/**
 * Turns poly's listeners on or off.
 */
function setPolyListeners(poly: BeyondMapsPoly, isListening: boolean) {
  MapService.setCursor('crosshair');
  Markers.setAllClickable(!isListening);
  Graphics.setAllClickable(!isListening);
  poly?.setListeners(isListening);
}

/**
 * Starts drawing a poly graphic, disable listeners
 */
function startDrawing(polyOptions: PolyOptions = {}) : BeyondMapsPoly {
  // @ts-ignore - poly is a reactive BeyondMapsPoly
  return reactive(new BeyondMapsPoly([], {
    type: 'polyline',
    leadingLine: true,
    ghostPolygon: false,
    editable: true,
    ...polyOptions,
  }));
}

function convertPoly(poly: BeyondMapsPoly, type: 'polygon'|'polyline') : PolyData {
  const vertices = poly.getVertices();

  const bounds = new gmAPI.LatLngBounds();
  vertices.forEach((vertex) => {
    bounds.extend(vertex);
  });
  // Calculate geohash and normalize bounds to JSON.
  const center = bounds.getCenter();
  const geohash = geohashForLocation([center.lat(), center.lng()]);
  const boundsJSON = bounds.toJSON();

  let name: string;
  let displayFields = [];
  // Initializing a nearly empty display field when creating a new poly graphic
  // lets `createFeatureContent` know to replace this with an aggregated value when the
  // info window is opened
  if (type === 'polygon') {
    name = 'Polygon';
    if (poly.calculateSF) {
      displayFields = displayFields = [new DisplayField({
        label: 'Calculated SF',
        unit: 'squareFeet',
        value: calculateSF([vertices]) || undefined,
        allowEdit: false,
        allowLabelEdit: false,
        allowDelete: true,
      })];
    }
    displayFields.push(new DisplayField({
      label: 'Acreage Calculated',
      unit: 'acreage',
      value: calculateTotalAcreage([vertices]) || undefined,
      allowEdit: false,
      allowLabelEdit: false,
      allowDelete: true,
    }));
  } else if (type === 'polyline') {
    name = 'Polyline';
    displayFields.push(new DisplayField({
      label: 'Calculated Distance',
      unit: 'miles',
      value: calculateTotalDistance(vertices) || undefined,
      allowEdit: false,
      allowLabelEdit: false,
      allowDelete: true,
    }));
  }

  // If we want new polygons/polylines to have starting display fields,
  // we should initialize them within this object.
  return {
    name,
    bounds: boundsJSON,
    displayFields,
    geohash,
    graphicData: {
      featureType: type,
      clickable: true,
      bounds: boundsJSON,
      path: vertices,
      strokeColor: poly.strokeColor || orange,
    },
  };
}

/**
 * Triggers subscribers to start listening for new poly graphic creation.
 */
function createNewPoly(callback: (polyData: PolyData) => void, polyOptions: PolyOptions = {}) : () => void {
  // Start drawing a poly
  const poly = startDrawing(polyOptions);
  setPolyListeners(poly, true);

  // Remove creation listeners and clear the temp poly
  const exitCreateMode = () => {
    setPolyListeners(poly, false);
    poly.remove();
    MapService.setCursor(null);
    window.removeEventListener('keydown', escapeListener);
  };

  // Handle when either poly type finishes creating
  const handleCreated = (type: 'polyline'|'polygon') => {
    callback(convertPoly(poly, type));
    exitCreateMode();
  };
  poly.subscribe('polygon_created', () => handleCreated('polygon'));
  poly.subscribe('polyline_created', () => handleCreated('polyline'));

  // Listen for a user pressing 'escape' to exit create mode
  const escapeListener = (event) => {
    if (event.key === 'Escape') {
      exitCreateMode();
    }
  };
  window.addEventListener('keydown', escapeListener, {once: true});
  // Return a function to exit create mode
  return exitCreateMode;
}

export {
  createNewPoly,
  // exported for tests
  setPolyListeners,
  startDrawing,
  convertPoly,
};
